import React, { useState, useRef, useEffect, useLayoutEffect } from 'react';
import '../styles/InfoButton.css';

interface InfoButtonProps {
  content: string;
  id: string;
}

const InfoButton: React.FC<InfoButtonProps> = ({ content, id }) => {
  const [isVisible, setIsVisible] = useState(false);
  const [tooltipStyle, setTooltipStyle] = useState<React.CSSProperties>({});
  const [isPositionReady, setIsPositionReady] = useState(false);
  const buttonRef = useRef<HTMLButtonElement>(null);
  const wrapperRef = useRef<HTMLSpanElement>(null);
  const hideTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        wrapperRef.current &&
        !wrapperRef.current.contains(event.target as Node)
      ) {
        setIsVisible(false);
      }
    };

    if (isVisible) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      if (hideTimeoutRef.current) {
        clearTimeout(hideTimeoutRef.current);
      }
    };
  }, [isVisible]);

  // Use useLayoutEffect to update position synchronously before paint
  useLayoutEffect(() => {
    if (!isVisible) {
      setTooltipStyle({});
      setIsPositionReady(false);
      return;
    }

    if (!buttonRef.current) return;

    const updateTooltipPosition = () => {
      if (!buttonRef.current) return;
      
      const buttonRect = buttonRef.current.getBoundingClientRect();
      const viewportWidth = window.innerWidth;
      
      // Check if mobile viewport
      const isMobile = viewportWidth <= 768;
      
      if (isMobile) {
        // Calculate tooltip width (ensure it fits viewport)
        const padding = 20;
        const maxWidth = viewportWidth - (padding * 2);
        const desiredWidth = Math.min(500, maxWidth);
        const tooltipWidth = Math.max(280, desiredWidth);
        
        // Calculate ideal center position based on button
        const buttonCenterX = buttonRect.left + (buttonRect.width / 2);
        const idealLeft = buttonCenterX - (tooltipWidth / 2);
        
        // Clamp to viewport bounds with padding
        const minLeft = padding;
        const maxLeft = viewportWidth - tooltipWidth - padding;
        const adjustedLeft = Math.max(minLeft, Math.min(idealLeft, maxLeft));
        
        // Calculate top position (below button)
        const topPosition = buttonRect.bottom + 8;
        
        // Set style immediately to avoid jumps
        setTooltipStyle({
          left: `${adjustedLeft}px`,
          top: `${topPosition}px`,
          transform: 'none',
          width: `${tooltipWidth}px`,
          maxWidth: `${tooltipWidth}px`,
        });
        setIsPositionReady(true);
      } else {
        // For desktop, keep centered above button (use CSS)
        setTooltipStyle({});
        setIsPositionReady(true);
      }
    };

    // Update position synchronously before paint
    updateTooltipPosition();
    
    // Update on scroll/resize with throttling
    let rafId: number | null = null;
    const throttledUpdate = () => {
      if (rafId) return;
      rafId = requestAnimationFrame(() => {
        updateTooltipPosition();
        rafId = null;
      });
    };
    
    window.addEventListener('scroll', throttledUpdate, true);
    window.addEventListener('resize', throttledUpdate);

    return () => {
      if (rafId) cancelAnimationFrame(rafId);
      window.removeEventListener('scroll', throttledUpdate, true);
      window.removeEventListener('resize', throttledUpdate);
    };
  }, [isVisible]);

  const handleMouseEnter = () => {
    if (hideTimeoutRef.current) {
      clearTimeout(hideTimeoutRef.current);
      hideTimeoutRef.current = null;
    }
    setIsPositionReady(false);
    setIsVisible(true);
  };

  const handleMouseLeave = () => {
    // Small delay before hiding to allow moving mouse to tooltip
    hideTimeoutRef.current = setTimeout(() => {
      setIsVisible(false);
    }, 100);
  };

  const handleToggle = () => {
    if (!isVisible) {
      setIsPositionReady(false);
    }
    setIsVisible(!isVisible);
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      handleToggle();
    } else if (e.key === 'Escape') {
      setIsVisible(false);
    }
  };

  const tooltipId = `${id}-tooltip`;

  return (
    <span 
      ref={wrapperRef}
      className="info-button-wrapper"
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
    >
      <button
        ref={buttonRef}
        type="button"
        className="info-button"
        aria-label="Show additional information"
        aria-expanded={isVisible}
        aria-describedby={isVisible ? tooltipId : undefined}
        onClick={handleToggle}
        onKeyDown={handleKeyDown}
      >
        <span className="info-icon" aria-hidden="true">ℹ️</span>
        <span className="sr-only">Information</span>
      </button>
      {isVisible && isPositionReady && (
        <div
          id={tooltipId}
          className="info-tooltip"
          role="tooltip"
          aria-live="polite"
          style={tooltipStyle}
        >
          {content}
        </div>
      )}
    </span>
  );
};

export default InfoButton;
